import { useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import {
  DropdownMenu,
  DropdownMenuCheckbox,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import SelectButton from "common/components/ui/SelectButton";

import type { WebhookEvent } from "../../types";

type WebhookEventsSelectProps = {
  events: string[] | WebhookEvent[];
  isLoading?: boolean;
} & (
  | {
      isMultiSelect: false;
      selection: string;
      setSelection: (newEvent: string) => void;
    }
  | {
      isMultiSelect: true;
      selection: string[];
      setSelection: (newEvents: string[]) => void;
    }
);

export default function WebhookEventsSelect({
  events,
  selection,
  setSelection,
  isMultiSelect,
  isLoading = false,
}: WebhookEventsSelectProps) {
  const [inputValue, setInputValue] = useState("");

  const triggerText = isMultiSelect === true ? selection.join(", ") : selection;
  const hasSelection =
    isMultiSelect === true ? !!selection.length : !!selection;

  const filteredEvents = events
    ? events.filter((event) => {
        const eventType = typeof event === "string" ? event : event.type;
        return eventType.includes(inputValue.trim().toLowerCase());
      })
    : [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SelectButton
          disabled={isLoading}
          size="md"
          hasSelection={hasSelection}
        >
          {hasSelection ? (
            <span className="line-clamp-1 break-all">{triggerText}</span>
          ) : (
            "Select events"
          )}
        </SelectButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        <MenuSearchInput
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <DropdownMenuList>
          {filteredEvents.map((event) => {
            const eventType = typeof event === "string" ? event : event.type;
            const isChecked =
              isMultiSelect === true
                ? !!selection.find((type) => type === eventType)
                : selection === event;

            return (
              <DropdownMenuItem
                key={eventType}
                onSelect={() => {
                  if (isMultiSelect === false) {
                    setSelection(eventType);
                  } else {
                    setSelection([eventType]);
                  }
                }}
              >
                {isMultiSelect && (
                  <DropdownMenuCheckbox
                    isChecked={isChecked}
                    onCheckedChange={() => {
                      setSelection(
                        isChecked
                          ? selection.filter((type) => type !== eventType)
                          : [...selection, eventType],
                      );
                    }}
                  />
                )}
                <div className="flex flex-col gap-0.5 overflow-hidden">
                  {eventType}
                  {typeof event !== "string" && (
                    <p className="text-left text-xs text-black-500">
                      {event.description}
                    </p>
                  )}
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
